import { ApplicationInsights } from '@microsoft/applicationinsights-web';

// Get company name from the current theme
const getCompanyName = () => {
  const theme = document.documentElement.getAttribute('data-theme');
  return theme || 'default';
};

// Global timer for app-wide timing
let appStartTime = null;

/**
 * Initialize the app timer
 */
export const startAppTimer = () => {
  appStartTime = Date.now();
};

/**
 * Get the current app duration in milliseconds
 * @returns {number} Duration in milliseconds
 */
export const getAppDuration = () => {
  if (!appStartTime) {
    console.warn('App timer not initialized');
    return 0;
  }
  return Date.now() - appStartTime;
};

/**
 * Track an event with default dimensions
 * @param {string} name - The name of the event
 * @param {Object} properties - Additional properties to track
 */
export const trackEvent = (name, properties = {}) => {
  if (!window.appInsights) {
    console.warn('Application Insights not initialized');
    return;
  }

  const defaultProperties = {
    companyName: getCompanyName(),
  };

  window.appInsights.trackEvent({
    name,
    properties: {
      ...defaultProperties,
      ...properties,
    },
  });
};

/**
 * Track a timing metric
 * @param {string} name - The name of the timing metric
 * @param {number} duration - The duration in milliseconds
 * @param {Object} properties - Additional properties to track
 */
export const trackTiming = (name, duration, properties = {}) => {
  if (!window.appInsights) {
    console.warn('Application Insights not initialized');
    return;
  }

  const defaultProperties = {
    companyName: getCompanyName(),
  };

  window.appInsights.trackMetric({
    name,
    average: duration,
    properties: {
      ...defaultProperties,
      ...properties,
    },
  });
};

/**
 * Track app-wide timing from start until now
 * @param {string} name - The name of the timing metric
 * @param {Object} properties - Additional properties to track
 */
export const trackAppTiming = (name, properties = {}) => {
  const duration = getAppDuration();
  trackTiming(name, duration, { ...properties, companyName: getCompanyName() });
};

/**
 * Initialize Application Insights
 * @param {string} instrumentationKey - The Application Insights instrumentation key
 */
export const initializeAppInsights = (instrumentationKey) => {
  if (instrumentationKey) {
    const appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: instrumentationKey,
        enableAutoRouteTracking: true,
        enableCorsCorrelation: true,
      },
    });

    appInsights.loadAppInsights();
    window.appInsights = appInsights;
  } else {
    console.warn('No instrumentation key provided');
  }
};
